import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, PhotoIcon } from '@heroicons/react/24/outline';

// Types
interface EnhancedPhoto {
  originalUrl: string;
  enhancedUrl: string;
  timestamp: Date;
  enhancementType: string;
  fileName: string;
}

// Enhancement options
const ENHANCEMENT_OPTIONS = [
  { value: 'hd', label: 'HD Enhancement', icon: '✨', description: 'Enhance overall quality and details' },
  { value: 'face', label: 'Face Enhancement', icon: '👤', description: 'Improve facial features and details' },
  { value: 'portrait', label: 'Portrait Pro', icon: '🎭', description: 'Professional portrait enhancement' },
  { value: 'restoration', label: 'Photo Restoration', icon: '🔄', description: 'Restore old or damaged photos' },
  { value: 'color', label: 'Color Enhancement', icon: '🎨', description: 'Improve colors and vibrancy' },
  { value: 'denoise', label: 'Noise Reduction', icon: '🔍', description: 'Remove noise and grain' }
];

// Utility function to format date
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

// Components
const Header: React.FC<{
  showHistory: boolean;
  onToggleHistory: () => void;
}> = ({ showHistory, onToggleHistory }) => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center space-x-4">
          <Link
            to="/generation"
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="h-3 w-3 bg-blue-500 rounded-full animate-pulse" />
            <h1 className="text-xl font-bold text-white">Photo HD Enhancement</h1>
          </div>
        </div>

        <button
          onClick={onToggleHistory}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            showHistory
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
          title={showHistory ? 'Hide History' : 'Show History'}
        >
          <ClockIcon className="w-5 h-5" />
          <span className="text-sm font-medium">History</span>
        </button>
      </div>
    </div>
  </div>
);

const PhotoUploader: React.FC<{
  onFileSelect: (file: File) => void;
  enhancementType: string;
  loading: boolean;
  onEnhancementChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}> = ({ onFileSelect, enhancementType, loading, onEnhancementChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      onFileSelect(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Enhancement Type
        </label>
        <select
          value={enhancementType}
          onChange={onEnhancementChange}
          className="w-full bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={loading}
        >
          <option value="">Select enhancement type...</option>
          {ENHANCEMENT_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.icon} {option.label}
            </option>
          ))}
        </select>
        {enhancementType && (
          <p className="mt-2 text-sm text-gray-400">
            {ENHANCEMENT_OPTIONS.find(o => o.value === enhancementType)?.description}
          </p>
        )}
      </div>

      <div
        className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          className="hidden"
        />
        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
        <div className="mt-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Select Photo
          </button>
          <p className="mt-2 text-sm text-gray-400">or drag and drop</p>
          <p className="mt-1 text-xs text-gray-500">PNG, JPG, WEBP up to 10MB</p>
        </div>
      </div>
    </div>
  );
};

const PhotoComparison: React.FC<{
  originalUrl: string;
  enhancedUrl: string;
  onDownload: () => void;
}> = ({ originalUrl, enhancedUrl, onDownload }) => (
  <div className="bg-gray-800 rounded-lg overflow-hidden">
    <div className="grid grid-cols-2 gap-4 p-4">
      <div>
        <p className="text-sm font-medium text-gray-400 mb-2">Original</p>
        <img src={originalUrl} alt="Original" className="w-full rounded-lg" />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-400 mb-2">Enhanced</p>
        <img src={enhancedUrl} alt="Enhanced" className="w-full rounded-lg" />
      </div>
    </div>
    <div className="p-4 border-t border-gray-700">
      <button
        onClick={onDownload}
        className="w-full py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
      >
        Download Enhanced Photo
      </button>
    </div>
  </div>
);

const HistorySidebar: React.FC<{
  show: boolean;
  items: EnhancedPhoto[];
  onClose: () => void;
  onDelete: (index: number) => void;
  onDownload: (photo: EnhancedPhoto) => void;
}> = ({ show, items, onClose, onDelete, onDownload }) => (
  <div
    className={`fixed inset-y-0 right-0 w-96 bg-gray-900 border-l border-gray-700 transform transition-transform duration-300 ease-in-out ${
      show ? 'translate-x-0' : 'translate-x-full'
    }`}
  >
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-medium text-white">History</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {items.map((item, index) => (
          <div key={index} className="bg-gray-800 rounded-lg overflow-hidden">
            <div className="grid grid-cols-2 gap-2 p-2">
              <img src={item.originalUrl} alt="Original" className="w-full h-24 object-cover rounded" />
              <img src={item.enhancedUrl} alt="Enhanced" className="w-full h-24 object-cover rounded" />
            </div>
            <div className="p-3 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500">{formatDate(item.timestamp)}</span>
                  <span className="ml-2 text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                    {ENHANCEMENT_OPTIONS.find(o => o.value === item.enhancementType)?.label}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onDownload(item)}
                    className="p-1.5 bg-green-500/20 hover:bg-green-500/40 rounded-lg text-green-400 hover:text-green-300 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDelete(index)}
                    className="p-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-800 mb-4">
              <ClockIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-300 mb-2">No enhanced photos yet</h3>
            <p className="text-gray-500">Upload a photo to get started</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const HDPhotoGeneration: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [enhancementType, setEnhancementType] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<EnhancedPhoto[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleEnhance = async () => {
    if (!selectedFile || !enhancementType || loading) return;

    setLoading(true);
    try {
      // For demo, we're using URL.createObjectURL
      // In production, you would upload to your server and process the image
      const originalUrl = URL.createObjectURL(selectedFile);
      
      // Simulate enhancement processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newItem: EnhancedPhoto = {
        originalUrl,
        // In production, this would be the URL of the processed image
        enhancedUrl: originalUrl,
        timestamp: new Date(),
        enhancementType,
        fileName: `enhanced_${selectedFile.name}`
      };
      
      setItems(prev => [newItem, ...prev]);
      setSelectedFile(null);
      setEnhancementType('');
    } catch (err) {
      console.error('Error enhancing photo:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (photo: EnhancedPhoto) => {
    try {
      const response = await fetch(photo.enhancedUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nexon-enhanced_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading photo:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200">
      <Header showHistory={showHistory} onToggleHistory={() => setShowHistory(!showHistory)} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">Photo HD Enhancement</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Transform your photos into stunning high-definition images.
            Enhance details, fix imperfections, and bring your memories to life.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-8">
          <PhotoUploader
            onFileSelect={handleFileSelect}
            enhancementType={enhancementType}
            loading={loading}
            onEnhancementChange={(e) => setEnhancementType(e.target.value)}
          />

          {selectedFile && enhancementType && (
            <div className="text-center">
              <button
                onClick={handleEnhance}
                disabled={loading}
                className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                  loading
                    ? 'bg-blue-500/50 text-blue-200 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {loading ? 'Enhancing...' : 'Enhance Photo'}
              </button>
            </div>
          )}

          <div className="space-y-4">
            {items.map((item, index) => (
              <PhotoComparison
                key={index}
                originalUrl={item.originalUrl}
                enhancedUrl={item.enhancedUrl}
                onDownload={() => handleDownload(item)}
              />
            ))}
          </div>
        </div>
      </div>

      <HistorySidebar
        show={showHistory}
        items={items}
        onClose={() => setShowHistory(false)}
        onDelete={(index) => setItems(items.filter((_, i) => i !== index))}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default HDPhotoGeneration;
